import React from 'react';

import CSSModules from './index.module.less';

import Compatibility from '@helpers/Compatibility';
import * as utils from './utils';
// import { getIOSVersion } from '@helpers/Common';

import ColumnItem from './ColumnItem';

import { IPickerColumnProps, IPickerColumnState } from './index.d';

//由于部分浏览器默认禁用root element的touch事件的preventDefault()效果
//因此在添加EventListener时，需要显式定义第三个传入参数的选项
const options = {
  capture: false,
  passive: false
} ;
class Column extends React.PureComponent<IPickerColumnProps, IPickerColumnState> {
  currentY: number;
  pageY: number;
  nextIndex: number;
  isInTransition: boolean;
  clicked: boolean;
  disableScroll: boolean;
  scroller: HTMLDivElement;
  maxNegativeY: number;
  moved = false;
  startTime: number;
  startY: number;
  constructor(props: IPickerColumnProps) {
    super(props);
    this.state = {
      index: props.initialIndex || 0
    };
    this.currentY = 0;
  }

  componentDidMount() {
    const { index } = this.state;
    this.touchEventDefault();
    if(index > 0 && this.getMaxIndex() >= index) {
      this.moveWithoutTransition(index);
    }
  }

  componentWillUnmount() {
    this.removeEventDefault();
  }

  touchEventDefault = () => {
    document.body.addEventListener('touchmove', this.prevent, options);
  }

  prevent = (event: any) => {
    event.preventDefault();
  }

  removeEventDefault = () => {
    document.body.removeEventListener('touchmove', this.prevent, options);
  }

  componentDidUpdate(prevProps: IPickerColumnProps) {
    //如果在列表被更新后，发现当前序号比列表的最大序号要大，则将序号和位置更新到最后一项
    if(
      prevProps.values !== this.props.values &&
      Array.isArray(this.props.values) &&
      this.state.index >= this.props.values.length
    ) {
      this.nextIndex = this.getMaxIndex();
      this.moveWithoutTransition(this.nextIndex);
      this.updateIndex();
    }
  }

  //由于fastclick的原因，事件的触发顺序是touchstart -> click -> touchend
  //(暂不考虑)如果移除fastclick，顺序会是touchstart -> touchend -> click
  handleChoose = (index: number) => {
    if(!this.isInTransition) {
      this.clicked = true;
      this.nextIndex = index;
    }
  }

  //所有用户交互逻辑，触发的第一个事件都会是touchstart，因此可以将初始化参数放到这里
  handleTouchStart = (event: React.TouchEvent) => {
    const { values } = this.props;
    this.disableScroll = !Array.isArray(values) || !values.length;
    if(this.disableScroll) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    //如果在触屏开始时正在执行过渡动画，将动画取消掉并停留在当前位置
    if(this.isInTransition) {
      this.scroller.style[Compatibility.css.transitionDuration] = null;
      this.scroller.style[Compatibility.css.transitionTimingFunction] = null;
      this.isInTransition = false;
      const computedY = utils.getComputedY(this.scroller);
      this.updateYOffset(computedY);
    }

    this.maxNegativeY = Math.round(this.getMaxIndex() * utils.height.unit * -1);
    this.moved = false;
    this.clicked = false;
    this.startY = this.currentY;
    this.pageY = event.touches[0].pageY;
    this.startTime = Date.now();
  }

  handleTouchMove = (event: React.TouchEvent) => {
    if(this.disableScroll) {
      return;
    }
    event.preventDefault();

    const currentTime = Date.now();
    const pageY = event.touches[0].pageY;
    const diffY = pageY - this.pageY;

    let y = this.currentY + diffY;
    if(y > 0 || y < this.maxNegativeY) {
      y = this.currentY + diffY / 3;
    }

    this.pageY = pageY;
    this.moved = true;
    this.updateYOffset(y);

    //用于判断momentum动画
    if(currentTime - this.startTime > 300) {
      this.startTime = currentTime;
      this.startY = y;
    }
  }

  handleTouchEnd = (event: React.TouchEvent) => {
    if(this.disableScroll) {
      return;
    }
    event.preventDefault();

    let y = Math.round(this.currentY);
    let duration = 300;
    let ease = utils.ease.circular;

    //如果点击某一项，则用动画移动到该项即可
    if(this.clicked && !this.moved) {
      y = this.nextIndex * utils.height.unit * -1;
      this.moveWithTransition(y, duration, ease);
      return;
    }

    //如果位置超出顶部或底部，则将位置重置到顶部或底部即可
    if(this.resetPosition()) {
      return;
    }

    //先将translateY设为整数
    this.updateYOffset(y);

    const diffTime = Date.now() - this.startTime;
    if(diffTime < 300) {
      const momentumY = utils.getMomentumY(y, this.startY, diffTime, this.maxNegativeY);
      y = momentumY.dest;
      duration = momentumY.duration < 300 ? duration : momentumY.duration;

      //如果momentum动画超出顶部或底部，则不需要算nextIndex
      //因为在动画结束时会再执行一次bounce动画，nextIndex会在那时候计算
      if(y > 0 || y < this.maxNegativeY) {
        ease = utils.ease.quadratic;
        this.moveWithTransition(y, duration, ease);
        return;
      }
    }

    const remainder = Math.abs(y % utils.height.unit);
    this.nextIndex = Math.floor(y / utils.height.unit) * -1;

    //如果无momentum动画而且当前位置刚好是某一项的中间，则只需要更新index
    if(y === this.currentY && !remainder) {
      this.updateIndex();
      return;
    }

    this.nextIndex -= remainder > utils.height.half ? 0 : 1;
    y = this.nextIndex * utils.height.unit * -1;
    this.moveWithTransition(y, duration, ease);
  }

  handleTransitionEnd = () => {
    if(!this.isInTransition) {
      return;
    }

    this.scroller.style[Compatibility.css.transitionDuration] = null;
    this.scroller.style[Compatibility.css.transitionTimingFunction] = null;

    //在过渡动画结束时，判断一下当前位置，目前仅会在momentum动画超出顶部或底部时返回true
    if(!this.resetPosition()) {
      this.isInTransition = false;
      this.updateIndex();
    }
  }

  getScrollerElement = (element: HTMLDivElement) => {
    this.scroller = element;
  }

  //判断当前位置是否超出顶部或底部，是则用bounce动画回退到顶部或底部
  resetPosition() {
    let y = this.currentY;
    if(y < this.maxNegativeY) {
      y = this.maxNegativeY;
      this.nextIndex = this.getMaxIndex();
    } else if(y > 0) {
      y = 0;
      this.nextIndex = 0;
    }

    if(y !== this.currentY) {
      this.moveWithTransition(y, 450, utils.ease.circular);
      return true;
    }
    return false;
  }

  moveWithTransition(y: number, duration: number, ease: string) {
    this.isInTransition = true;
    this.scroller.style[Compatibility.css.transitionDuration] = duration + 'ms';
    this.scroller.style[Compatibility.css.transitionTimingFunction] = ease;
    this.updateYOffset(y);
  }

  moveWithoutTransition(index: number) {
    const y = Math.round(index * utils.height.unit * -1);
    this.updateYOffset(y);
  }

  updateYOffset(y = 0) {
    if(this.scroller) {
      this.scroller.style[Compatibility.css.transform] = `translate3d(0, ${ y }px, 0)`;
      this.currentY = y;
    }
  }

  updateIndex() {
    if(typeof this.nextIndex === 'number') {
      this.setState({ index: this.nextIndex });
      this.props.onChosen(this.props.index, this.nextIndex);
      this.nextIndex = null;
    }
  }

  getMaxIndex() {
    const { values } = this.props;
    if(Array.isArray(values)) {
      const maxIndex = values.length - 1;
      return maxIndex < 0 ? 0 : maxIndex;
    }
    return 0;
  }

  render() {
    const { values, columnWidth } = this.props;
    return (
      <div
        className={ columnWidth ? null : CSSModules.equal }
        styleName="column"
        style={{ width: columnWidth }}
        onTouchStart={ this.handleTouchStart }
        onTouchMove={ this.handleTouchMove }
        onTouchEnd={ this.handleTouchEnd }
        onTouchCancel={ this.handleTouchEnd }
      >
        <div
          styleName="scroller"
          onTransitionEnd={ this.handleTransitionEnd }
          ref={ this.getScrollerElement }
        >
          {
            values.map((value, index) => (
              <ColumnItem
                key={ value + '-' + index }
                index={ index }
                value={ value }
                active={ this.state.index === index }
                // onChoose={ this.handleChoose }
              />
            ))
          }
        </div>
      </div>
    );
  }
}

export default Column
